import { GenericObject } from "@taager/reinforcements";

// Configuration for fast encryption
const ALGORITHM = "AES-CBC"; // Web Crypto API algorithm name
const STATIC_KEY_STRING = "__TAAGEr__2030@_"; // 16 chars for 128-bit key
const IV_LENGTH = 16;

// Helper: Convert string to Uint8Array
function strToUint8Array(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

// Helper: Convert Uint8Array to string
function uint8ArrayToStr(arr: Uint8Array): string {
  return new TextDecoder().decode(arr);
}

// Helper: Base64 encode/decode (URL-safe)
function toUrlSafeBase64(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromUrlSafeBase64(input: string): Uint8Array {
  input = input.replace(/-/g, "+").replace(/_/g, "/");
  // Pad base64 string
  while (input.length % 4) input += "=";
  const binary = atob(input);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// Helper: Generate random IV
function randomIV(): Uint8Array {
  const iv = new Uint8Array(IV_LENGTH);
  window.crypto.getRandomValues(iv);
  return iv;
}

// Helper: Import static key for Web Crypto
async function getCryptoKey(): Promise<CryptoKey> {
  return await window.crypto.subtle.importKey(
    "raw",
    strToUint8Array(STATIC_KEY_STRING),
    { name: ALGORITHM },
    false,
    ["encrypt", "decrypt"],
  );
}

// Fast encrypt object to URL-safe string (browser)
export async function encryptValue<T = GenericObject>(
  value: T,
): Promise<string> {
  try {
    const iv = randomIV();
    const key = await getCryptoKey();
    const text = JSON.stringify(value);
    const textBytes = strToUint8Array(text);
    const encrypted = new Uint8Array(
      await window.crypto.subtle.encrypt(
        { name: ALGORITHM, iv },
        key,
        textBytes,
      ),
    );
    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encrypted.length);
    combined.set(iv, 0);
    combined.set(encrypted, iv.length);
    return toUrlSafeBase64(combined);
  } catch (error: any) {
    throw new Error(`Encryption failed: ${error.message}`);
  }
}

// Fast decrypt URL-safe string to object (browser)
export async function decryptValue<T>(encrypted: string): Promise<T> {
  try {
    const combined = fromUrlSafeBase64(encrypted);
    const iv = combined.subarray(0, IV_LENGTH);
    const encryptedData = combined.subarray(IV_LENGTH);
    const key = await getCryptoKey();
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: ALGORITHM, iv },
      key,
      encryptedData,
    );
    const decrypted = uint8ArrayToStr(new Uint8Array(decryptedBuffer));
    return JSON.parse(decrypted) as T;
  } catch (error: any) {
    throw new Error(`Decryption failed: ${error.message}`);
  }
}
